import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from '../supabase';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { MAINTENANCE } from '../config';
import '../design-system.css';
import { useSiteLang as useLang } from '../siteLang';



function AuthorLink({ username, label, navigate, style, children }) {
    return (
        <span
            onClick={(e) => { e.stopPropagation(); navigate(`/creator/${username}`); }}
            style={{ cursor: 'pointer', ...style }}
            onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
        >
            {children ?? label}
        </span>
    );
}

function Blog() {
    const { lang } = useLang();
    const t = {
        fr: { eyebrow: 'blog.ie', title: 'Déclarations officielles et actualités quotidiennes' },
        en: { eyebrow: 'blog.ie', title: 'Official statements and day-to-day updates' },
        vi: { eyebrow: 'blog.ie', title: 'Thông báo chính thức và cập nhật hàng ngày' },
    }[lang] || { eyebrow: 'blog.ie', title: '...' };
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [usersMap, setUsersMap] = useState({});

    useEffect(() => { document.title = 'blog.ie | winston.ie'; }, []);

    useEffect(() => {
        const fetchPosts = async () => {
            const { data } = await supabase
                .from('posts')
                .select('*')
                .order('created_at', { ascending: false });

            if (data) {
                setPosts(data);
                const usernames = new Set();
                data.forEach(p => {
                    if (p.author_username) usernames.add(p.author_username);
                    (p.co_authors || []).forEach(u => usernames.add(u));
                });
                if (usernames.size > 0) {
                    const { data: usersData } = await supabase
                        .from('users')
                        .select('username, first_name, last_name')
                        .in('username', Array.from(usernames));
                    if (usersData) {
                        const map = {};
                        usersData.forEach(u => { map[u.username] = u; });
                        setUsersMap(map);
                    }
                }
            }
        };
        fetchPosts();
    }, []);

    const displayName = (username, fallback) => {
        const u = usersMap[username];
        if (!u) return fallback || `@${username}`;
        return `${u.first_name || ''} ${u.last_name || ''}`.trim() || `@${username}`;
    };

    const renderAuthors = (post) => {
        const authors = [
            { username: post.author_username, label: displayName(post.author_username, post.author) },
            ...(post.co_authors || []).map(u => ({ username: u, label: displayName(u) })),
        ].filter(a => a.username);

        return authors.map((a, i) => (
            <span key={a.username}>
                {i > 0 && ' & '}
                <AuthorLink username={a.username} label={a.label} navigate={navigate} />
            </span>
        ));
    };

    const featured = posts[0] || null;
    const rest = posts.slice(1);
    const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const UI_FONT = 'Helvetica Neue, Arial, sans-serif';
    const color = '#333333';

    if (MAINTENANCE) return (
        <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <video autoPlay loop muted playsInline style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: -1 }}>
                <source src="/video/background.mp4" type="video/mp4" />
            </video>
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: -1 }} />
            <div style={{ textAlign: 'center' }}>
                <p style={{ fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: '12px' }}>winston.ie</p>
                <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', fontWeight: '300', letterSpacing: '0.02em' }}>Quietly reorganizing. Back soon.</p>
            </div>
        </div>
    );

    return (
        <div className="w-page">
            <Navbar variant="light" />

            <div className="w-body">
                <p className="w-eyebrow">{t.eyebrow}</p>
                <h1 style={{
                    background: 'none', border: 'none', padding: 0,
                    fontFamily: UI_FONT, fontWeight: 300,
                    fontSize: 'clamp(30px, 6vw, 52px)', letterSpacing: '-0.01em',
                    lineHeight: 1, color,
                }}>{t.title}</h1>
                {posts.length === 0 ? (
                    <p className="w-subtitle" style={{ marginTop: '24px' }}>No posts yet.</p>
                ) : (
                    <>
                        {featured && (
                            <>
                                <p className="w-eyebrow" style={{ marginTop: '36px' }}>Recent post</p>
                                <div
                                    className="w-album-card"
                                    style={{ display: 'flex', maxWidth: 'none', marginBottom: '40px' }}
                                    onClick={() => navigate(`/blog/${featured.slug}`)}
                                >
                                    {featured.cover_image && (
                                        <div style={{ width: '46%', flexShrink: 0, background: 'var(--w-bg-soft)', overflow: 'hidden' }}>
                                            <img src={featured.cover_image} alt={featured.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                        </div>
                                    )}
                                    <div style={{ padding: '28px 30px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '10px' }}>
                                        <div style={{ fontSize: '11px', color: 'var(--w-muted)' }}>
                                            {formatDate(featured.created_at)} · {renderAuthors(featured)}
                                            {featured.is_statement && <span style={{ marginLeft: 8, color: 'var(--w-accent)', fontWeight: 600 }}>● OFFICIAL STATEMENT</span>}
                                        </div>
                                        <div style={{ fontSize: '22px', fontWeight: 600, color: 'var(--w-ink)', lineHeight: 1.3, letterSpacing: '-0.01em' }}>{featured.title}</div>
                                        <div style={{ fontSize: '13px', color: 'var(--w-ink-soft)', lineHeight: 1.7 }}>{featured.content?.substring(0, 160)}...</div>
                                        <div style={{ fontSize: '10.5px', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--w-muted)' }}>Read →</div>
                                    </div>
                                </div>
                            </>
                        )}

                        {rest.length > 0 && (
                            <>
                                <p className="w-eyebrow">All posts</p>
                                <div className="w-album-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                                    {rest.map((post) => (
                                        <div key={post.slug} className="w-album-card" onClick={() => navigate(`/blog/${post.slug}`)}>
                                            {post.cover_image && (
                                                <div style={{ height: '150px', background: 'var(--w-bg-soft)', overflow: 'hidden' }}>
                                                    <img src={post.cover_image} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                                                </div>
                                            )}
                                            <div style={{ padding: '16px 18px 18px' }}>
                                                <div style={{ fontSize: '10px', color: 'var(--w-muted)', marginBottom: '8px' }}>
                                                    {formatDate(post.created_at)} · {renderAuthors(post)}
                                                    {post.is_statement && <span style={{ marginLeft: 6, color: 'var(--w-accent)', fontWeight: 600 }}>● OFFICIAL</span>}
                                                </div>
                                                <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--w-ink)', lineHeight: 1.35, marginBottom: '6px' }}>{post.title}</div>
                                                <div style={{ fontSize: '11.5px', color: 'var(--w-ink-soft)', lineHeight: 1.6 }}>{post.content?.substring(0, 100)}...</div>
                                                <div style={{ fontSize: '10px', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--w-muted)', marginTop: '10px' }}>Read →</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            <Footer variant="light" />
        </div>
    );
}

export default Blog;